/**
 * HTTP 请求日志记录器
 * 用于记录所有 HTTP 请求和响应的详细信息，便于开发调试
 */

import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * HTTP 日志记录器类
 * 提供请求、响应和错误的详细日志记录功能
 */
class HttpLogger {
  /**
   * 是否启用日志
   * 在生产环境下应该关闭，避免敏感信息泄露
   */
  private enabled: boolean;

  /**
   * 请求计时器存储
   * key: 请求ID, value: 开始时间戳
   */
  private timers: Map<string, number>;

  /**
   * 请求计数器
   * 用于生成唯一的请求ID
   */
  private requestCounter: number;

  constructor() {
    // 根据环境变量或开发环境判断是否启用日志
    this.enabled = import.meta.env.MODE === 'development' || import.meta.env.VITE_ENABLE_HTTP_LOG === 'true';
    this.timers = new Map();
    this.requestCounter = 0;
  }

  /**
   * 记录 HTTP 请求
   * @param config - Axios 请求配置
   * @returns 返回请求ID，用于关联响应
   */
  request(config: AxiosRequestConfig): string {
    if (!this.enabled) return '';

    const requestId = this.generateId();
    const timestamp = Date.now();
    this.timers.set(requestId, timestamp);

    // 为请求添加ID，便于追踪
    if (config.headers) {
      config.headers['X-Request-Id'] = requestId;
    }

    const method = config.method?.toUpperCase() || 'GET';
    const url = this.formatUrl(config);

    // 使用 console.group 创建可折叠的日志组
    console.group(
      `%c📤 [HTTP Request #${requestId}] ${method} ${config.url}`,
      'color: #0ea5e9; font-weight: bold;'
    );

    // 时间信息
    console.log(
      `%c⏰ Time:`,
      'color: #6b7280; font-weight: bold;',
      new Date(timestamp).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    );

    // 完整URL
    console.log(
      `%c🔗 URL:`,
      'color: #6b7280; font-weight: bold;',
      url
    );

    // 查询参数
    if (config.params && Object.keys(config.params).length > 0) {
      console.log(
        `%c📝 Params:`,
        'color: #6b7280; font-weight: bold;',
        config.params
      );
    }

    // 请求体
    if (config.data) {
      const data = this.formatRequestData(config.data);
      console.log(
        `%c📦 Body:`,
        'color: #6b7280; font-weight: bold;',
        data
      );
    }

    // 请求头
    if (config.headers) {
      const headers = this.formatHeaders(config.headers);
      console.log(
        `%c🎫 Headers:`,
        'color: #6b7280; font-weight: bold;',
        headers
      );
    }

    // 生成 cURL 命令，便于复制调试
    const curl = this.generateCurl(config);
    if (curl) {
      console.log(
        `%c📋 cURL:`,
        'color: #6b7280; font-weight: bold;',
        curl
      );
    }

    console.groupEnd();

    return requestId;
  }

  /**
   * 记录 HTTP 响应
   * @param response - Axios 响应对象
   */
  response(response: AxiosResponse): void {
    if (!this.enabled) return;

    const requestId = response.config?.headers?.['X-Request-Id'] as string || '';
    const startTime = this.timers.get(requestId);
    const duration = startTime ? Date.now() - startTime : 0;

    // 清理计时器
    if (requestId) {
      this.timers.delete(requestId);
    }

    const method = response.config.method?.toUpperCase() || 'GET';
    const statusText = this.getStatusText(response.status);

    // 使用不同颜色区分成功响应
    console.group(
      `%c📥 [HTTP Response #${requestId}] ${method} ${response.config.url}`,
      'color: #10b981; font-weight: bold;'
    );

    // 状态码
    console.log(
      `%c✅ Status:`,
      'color: #6b7280; font-weight: bold;',
      `${response.status} ${statusText}`
    );

    // 响应时间
    if (duration > 0) {
      const formattedDuration = this.formatMilliseconds(duration);
      const durationColor = duration < 500 ? '#10b981' : duration < 1000 ? '#f59e0b' : '#ef4444';
      console.log(
        `%c⏱️ Duration:`,
        'color: #6b7280; font-weight: bold;',
        `%c${formattedDuration}`,
        `color: ${durationColor}; font-weight: bold;`
      );
    }

    // 响应数据大小
    const dataSize = this.calculateDataSize(response.data);
    if (dataSize) {
      console.log(
        `%c📊 Size:`,
        'color: #6b7280; font-weight: bold;',
        this.formatBytes(dataSize)
      );
    }

    // 响应数据
    console.log(
      `%c📦 Data:`,
      'color: #6b7280; font-weight: bold;',
      response.data
    );

    // 响应头（可选，通常在调试时需要）
    if (import.meta.env.VITE_LOG_RESPONSE_HEADERS === 'true') {
      console.log(
        `%c🎫 Response Headers:`,
        'color: #6b7280; font-weight: bold;',
        response.headers
      );
    }

    console.groupEnd();
  }

  /**
   * 记录 HTTP 错误
   * @param error - Axios 错误对象
   */
  error(error: AxiosError): void {
    if (!this.enabled) return;

    const requestId = error.config?.headers?.['X-Request-Id'] as string || '';
    const startTime = this.timers.get(requestId);
    const duration = startTime ? Date.now() - startTime : 0;

    // 清理计时器
    if (requestId) {
      this.timers.delete(requestId);
    }

    const method = error.config?.method?.toUpperCase() || 'GET';
    const url = error.config?.url || 'Unknown URL';

    // 使用红色突出显示错误
    console.group(
      `%c❌ [HTTP Error #${requestId}] ${method} ${url}`,
      'color: #ef4444; font-weight: bold;'
    );

    // 错误信息
    console.error(
      `%c🚨 Error:`,
      'color: #6b7280; font-weight: bold;',
      error.message
    );

    // 响应时间（如果有）
    if (duration > 0) {
      console.log(
        `%c⏱️ Duration:`,
        'color: #6b7280; font-weight: bold;',
        this.formatMilliseconds(duration)
      );
    }

    // 如果有响应
    if (error.response) {
      console.error(
        `%c📍 Status:`,
        'color: #6b7280; font-weight: bold;',
        `${error.response.status} ${this.getStatusText(error.response.status)}`
      );

      console.error(
        `%c💬 Response:`,
        'color: #6b7280; font-weight: bold;',
        error.response.data
      );

      // 响应头（调试时可能需要）
      if (import.meta.env.VITE_LOG_ERROR_HEADERS === 'true') {
        console.error(
          `%c🎫 Response Headers:`,
          'color: #6b7280; font-weight: bold;',
          error.response.headers
        );
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应
      console.error(
        `%c📡 Request:`,
        'color: #6b7280; font-weight: bold;',
        'No response received'
      );
      console.error(
        `%c🔍 Details:`,
        'color: #6b7280; font-weight: bold;',
        error.request
      );
    } else {
      // 请求配置出错
      console.error(
        `%c⚙️ Config Error:`,
        'color: #6b7280; font-weight: bold;',
        'Request setup failed'
      );
    }

    // 错误堆栈（开发环境显示）
    if (import.meta.env.MODE === 'development' && error.stack) {
      console.error(
        `%c📚 Stack:`,
        'color: #6b7280; font-weight: bold;',
        error.stack
      );
    }

    console.groupEnd();
  }

  /**
   * 生成唯一的请求ID
   */
  private generateId(): string {
    this.requestCounter++;
    return this.requestCounter.toString();
  }

  /**
   * 格式化URL（组合baseURL和url）
   */
  private formatUrl(config: AxiosRequestConfig): string {
    const baseURL = config.baseURL || '';
    const url = config.url || '';

    // 如果 url 已经是完整的 URL
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // 组合 baseURL 和 url
    return `${baseURL}${url}`.replace(/\/+/g, '/').replace(':/', '://');
  }

  /**
   * 格式化请求数据（隐藏敏感信息）
   */
  private formatRequestData(data: any): any {
    if (!data) return data;

    // 如果是 FormData
    if (data instanceof FormData) {
      const formDataObj: Record<string, any> = {};
      data.forEach((value, key) => {
        formDataObj[key] = value instanceof File ? `File: ${value.name}` : value;
      });
      return formDataObj;
    }

    // 如果是对象，隐藏密码等敏感字段
    if (typeof data === 'object') {
      const cloned = JSON.parse(JSON.stringify(data));
      const sensitiveFields = ['password', 'pwd', 'token', 'secret', 'key', 'authorization'];

      const hideSensitive = (obj: any) => {
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            const lowerKey = key.toLowerCase();
            if (sensitiveFields.some(field => lowerKey.includes(field))) {
              obj[key] = '***';
            } else if (typeof obj[key] === 'object' && obj[key] !== null) {
              hideSensitive(obj[key]);
            }
          }
        }
      };

      hideSensitive(cloned);
      return cloned;
    }

    return data;
  }

  /**
   * 格式化请求头（隐藏敏感信息）
   */
  private formatHeaders(headers: any): Record<string, string> {
    const formatted: Record<string, string> = {};
    const sensitiveHeaders = ['authorization', 'cookie', 'x-api-key', 'x-auth-token'];

    for (const key in headers) {
      if (headers.hasOwnProperty(key)) {
        const lowerKey = key.toLowerCase();
        const value = headers[key];

        // 跳过某些内部header
        if (lowerKey === 'x-request-id') continue;

        // 隐藏敏感header的值
        if (sensitiveHeaders.includes(lowerKey)) {
          if (typeof value === 'string' && value.length > 10) {
            formatted[key] = `${value.substring(0, 10)}...`;
          } else {
            formatted[key] = '***';
          }
        } else {
          formatted[key] = value;
        }
      }
    }

    return formatted;
  }

  /**
   * 生成 cURL 命令（便于复制调试）
   */
  private generateCurl(config: AxiosRequestConfig): string {
    const method = config.method?.toUpperCase() || 'GET';
    const url = this.formatUrl(config);
    let curl = `curl -X ${method} '${url}'`;

    // 添加请求头
    if (config.headers) {
      for (const [key, value] of Object.entries(config.headers)) {
        if (key.toLowerCase() !== 'x-request-id' && value) {
          // 对敏感header进行处理
          const displayValue = key.toLowerCase() === 'authorization' && typeof value === 'string'
            ? `${value.substring(0, 20)}...`
            : value;
          curl += ` \\\n  -H '${key}: ${displayValue}'`;
        }
      }
    }

    // 添加请求体
    if (config.data && method !== 'GET') {
      if (config.data instanceof FormData) {
        curl += ` \\\n  -F '...(FormData)'`;
      } else {
        const dataStr = JSON.stringify(config.data);
        curl += ` \\\n  -d '${dataStr}'`;
      }
    }

    // 添加查询参数
    if (config.params && Object.keys(config.params).length > 0) {
      const queryString = new URLSearchParams(config.params).toString();
      if (url.includes('?')) {
        curl = curl.replace(url, `${url}&${queryString}`);
      } else {
        curl = curl.replace(url, `${url}?${queryString}`);
      }
    }

    return curl;
  }

  /**
   * 格式化字节大小
   * @param bytes - 字节数
   * @returns 格式化后的字符串（如: 1.2 KB）
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  /**
   * 格式化毫秒时间
   * @param ms - 毫秒数
   * @returns 格式化后的字符串
   */
  private formatMilliseconds(ms: number): string {
    if (ms < 1000) {
      return `${ms}ms`;
    } else if (ms < 60000) {
      return `${(ms / 1000).toFixed(2)}s`;
    } else {
      const minutes = Math.floor(ms / 60000);
      const seconds = ((ms % 60000) / 1000).toFixed(0);
      return `${minutes}m ${seconds}s`;
    }
  }

  /**
   * 计算数据大小
   */
  private calculateDataSize(data: any): number {
    if (!data) return 0;

    try {
      const str = typeof data === 'string' ? data : JSON.stringify(data);
      return new Blob([str]).size;
    } catch {
      return 0;
    }
  }

  /**
   * 获取状态码文本描述
   */
  private getStatusText(status: number): string {
    const statusTexts: Record<number, string> = {
      200: 'OK',
      201: 'Created',
      204: 'No Content',
      301: 'Moved Permanently',
      302: 'Found',
      304: 'Not Modified',
      400: 'Bad Request',
      401: 'Unauthorized',
      403: 'Forbidden',
      404: 'Not Found',
      405: 'Method Not Allowed',
      409: 'Conflict',
      422: 'Unprocessable Entity',
      429: 'Too Many Requests',
      500: 'Internal Server Error',
      502: 'Bad Gateway',
      503: 'Service Unavailable',
      504: 'Gateway Timeout'
    };

    return statusTexts[status] || '';
  }

  /**
   * 启用日志
   */
  enable(): void {
    this.enabled = true;
    console.log('%c✅ HTTP Logger Enabled', 'color: #10b981; font-weight: bold;');
  }

  /**
   * 禁用日志
   */
  disable(): void {
    this.enabled = false;
    console.log('%c🔇 HTTP Logger Disabled', 'color: #6b7280; font-weight: bold;');
  }

  /**
   * 清理所有计时器
   */
  clear(): void {
    this.timers.clear();
    this.requestCounter = 0;
  }
}

// 导出单例实例
export const httpLogger = new HttpLogger();

// 导出类（如果需要创建多个实例）
export default HttpLogger;
